import { Pause, Play, RotateCcw, Volume2 } from 'lucide-react-native';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';

interface VoicePreviewPlayerProps {
  audioUrl: string | null;
  audioBlob?: Blob | null;
  isLoading?: boolean;
  onRetry?: () => void;
  compact?: boolean;
}

function formatTime(time: number) {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function resolveAudioSrc(
  audioBlob: Blob | null | undefined,
  audioUrl: string | null,
  blobUrlRef: RefObject<string | null>,
): string | null {
  if (blobUrlRef.current) {
    URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = null;
  }

  if (audioBlob) {
    const src = URL.createObjectURL(audioBlob);
    blobUrlRef.current = src;
    return src;
  }

  return audioUrl;
}

function renderNonWebPlatform(compact: boolean, t: (key: string) => string) {
  return (
    <Box
      backgroundColor={colors.background.secondary}
      borderRadius="md"
      padding={compact ? 'sm' : 'md'}
    >
      <HStack spacing="sm" align="center">
        <Volume2 size={compact ? 16 : 20} color={colors.text.tertiary} />
        <Text variant="bodySmall" color={colors.text.tertiary}>
          {t('voice.player.webOnly')}
        </Text>
      </HStack>
    </Box>
  );
}

function renderError(error: string, compact: boolean, onRetry?: () => void) {
  return (
    <Box backgroundColor={colors.status.error.bg} borderRadius="md" padding={compact ? 'sm' : 'md'}>
      <HStack spacing="sm" align="center" justify="between">
        <Text variant="bodySmall" color={colors.status.error.main}>
          {error}
        </Text>
        {onRetry && (
          <Button variant="ghost" size="sm" onPress={onRetry}>
            <RotateCcw size={14} color={colors.status.error.main} />
          </Button>
        )}
      </HStack>
    </Box>
  );
}

function renderPlayButton(
  isPlaying: boolean,
  hasAudio: boolean,
  isLoading: boolean,
  compact: boolean,
  togglePlay: () => void,
) {
  return (
    <Button
      variant="primary"
      size="sm"
      onPress={togglePlay}
      disabled={!hasAudio || isLoading}
      style={{
        width: compact ? 32 : 40,
        height: compact ? 32 : 40,
        borderRadius: 20,
        padding: 0,
      }}
    >
      {isPlaying ? (
        <Pause size={compact ? 14 : 18} color={colors.primary[900]} />
      ) : (
        <Play size={compact ? 14 : 18} color={colors.primary[900]} />
      )}
    </Button>
  );
}

function renderProgressBar(progress: number, currentTime: number, duration: number) {
  return (
    <VStack spacing="xxs" style={{ flex: 1 }}>
      <Box
        backgroundColor={colors.border.muted}
        borderRadius="full"
        style={{ height: 4, overflow: 'hidden' }}
      >
        <Box
          backgroundColor={colors.primary.main}
          style={{
            width: `${progress}%`,
            height: '100%',
            borderRadius: 2,
          }}
        />
      </Box>
      <HStack justify="between">
        <Text variant="caption" color={colors.text.tertiary}>
          {formatTime(currentTime)}
        </Text>
        <Text variant="caption" color={colors.text.tertiary}>
          {formatTime(duration)}
        </Text>
      </HStack>
    </VStack>
  );
}

function renderRetryButton(onRetry?: () => void, isLoading?: boolean) {
  if (!onRetry) return null;

  return (
    <Button variant="ghost" size="sm" onPress={onRetry} disabled={isLoading}>
      <RotateCcw size={14} color={colors.text.secondary} />
    </Button>
  );
}

export function VoicePreviewPlayer({
  audioUrl,
  audioBlob,
  isLoading = false,
  onRetry,
  compact = false,
}: Readonly<VoicePreviewPlayerProps>) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const audio = new Audio();
    audioRef.current = audio;

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
      setError(null);
    });
    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });
    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentTime(0);
    });
    audio.addEventListener('error', () => {
      setError(t('voice.player.error'));
      setIsPlaying(false);
    });

    setIsReady(true);

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, [t]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !isReady || !audioRef.current) return;

    const src = resolveAudioSrc(audioBlob, audioUrl, blobUrlRef);

    if (src) {
      setError(null);
      audioRef.current.src = src;
      audioRef.current.load();
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [audioUrl, audioBlob, isReady]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {
        setError(t('voice.player.failedToPlay'));
      });
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, t]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const hasAudio = Boolean(audioUrl || audioBlob);

  if (Platform.OS !== 'web') {
    return renderNonWebPlatform(compact, t);
  }

  if (error) {
    return renderError(error, compact, onRetry);
  }

  return (
    <Box
      backgroundColor={colors.background.secondary}
      borderRadius="md"
      padding={compact ? 'sm' : 'md'}
    >
      <VStack spacing={compact ? 'xs' : 'sm'}>
        <HStack spacing="md" align="center">
          {renderPlayButton(isPlaying, hasAudio, isLoading, compact, togglePlay)}
          {renderProgressBar(progress, currentTime, duration)}
          {renderRetryButton(onRetry, isLoading)}
        </HStack>

        {isLoading && (
          <Text variant="caption" color={colors.text.tertiary}>
            {t('voice.player.generating')}
          </Text>
        )}
      </VStack>
    </Box>
  );
}
